import { useQuery } from '@apollo/client'
import Hidden from '@mui/material/Hidden'
import { useFlag } from '@unleash/proxy-client-react'
import { getAuth } from 'firebase/auth'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BucketsByUserIdDocument, IntegrationStatus, IntegrationType, UserProfileFragmentFragmentDoc } from '../../../../graphql/generated'
import usePendoWhatsAppTracking from '../../../../hooks/usePendoWhatsAppTracking'
import cache, { drawerVar, modalVar } from '../../../../providers/apollo/cache'
import logger from '../../../../utils/logger'
import useBucket from '../../hooks/useBucket'
import useProfilePlan from '../../hooks/useProfilePlan'
import useUserSubscription from '../../hooks/useUserSubcription'
import DesktopDrawer from './desktop'
import MobileDrawer from './mobile'

export default function Drawer() {
  const queryParams = useParams<{ bucketId: string }>()
  const navigate = useNavigate()
  const [drawerSection, setDrawerSection] = useState<'main' | 'albums'>('main')
  const { bucket, bucketAlbums, fetchMore, loading, loadingMore } = useBucket()
  const useWhatsAppIntegration = useFlag('useWhatsAppIntegration')
  const userId = getAuth().currentUser?.uid
  const { trackWhatsAppEvent } = usePendoWhatsAppTracking()
  const { isSubscriptionActive } = useUserSubscription()
  const { isFreemiumUser, bucketLimit } = useProfilePlan()

  const { data: bucketsData } = useQuery(BucketsByUserIdDocument, {
    variables: { userId: userId! },
    skip: !userId
  })

  const allBuckets = bucketsData?.bucketsByUserId?.items || []
  const freemiumUserReachedMaxBuckets = isFreemiumUser && allBuckets.filter((b) => b.ownerId === userId).length >= bucketLimit
  const canCreateBucket = isSubscriptionActive || isFreemiumUser

  const userProfile = cache.readFragment({
    id: `UserProfile:${userId}`,
    fragmentName: 'UserProfileFragment',
    fragment: UserProfileFragmentFragmentDoc
  })
  const hasActiveWhatsAppIntegration = userProfile?.integrations?.some(
    (integration) => integration?.integrationType === IntegrationType.Whatsapp && integration?.status === IntegrationStatus.Active
  )
  const isWhatsAppEnabled = userProfile?.plan?.is_whatsapp_enabled
  const isEmailVerified = getAuth().currentUser?.emailVerified
  const showWhatsAppButton = !bucket?.isBanned && useWhatsAppIntegration && isEmailVerified && isWhatsAppEnabled

  const albums = bucketAlbums?.items || []
  const nextToken = bucketAlbums?.nextToken

  const results = useQuery(BucketsByUserIdDocument, {
    skip: !userId,
    variables: { userId: userId! },
    onError: (e) => {
      logger.error(e)
    }
  })
  const bucketId = queryParams.bucketId ?? results.data?.userProfile?.currentBucketId

  const openModal = (modalName: 'createBucket' | 'bucketRename' | 'upsellModal') => {
    drawerVar(null)
    modalVar(modalName)
  }

  const handleOpenAlbums = () => setDrawerSection('albums')
  const handleBack = () => setDrawerSection('main')

  const createBucket = () => {
    if (freemiumUserReachedMaxBuckets) {
      openModal('upsellModal')
      return
    }
    openModal('createBucket')
  }

  const onClickWhatsApp = () => {
    const buttonText = hasActiveWhatsAppIntegration ? 'Manage WhatsApp' : 'Connect to WhatsApp'
    trackWhatsAppEvent('whatsapp_sidebar_connect_clicked', {
      platform: 'web',
      user_has_integration: hasActiveWhatsAppIntegration,
      button_text: buttonText,
      source_page: window.location.pathname,
      bucket_id: bucketId
    })

    let redirectUrl = `/integrations/whatsapp/setup`
    if (hasActiveWhatsAppIntegration) {
      redirectUrl = `/integrations/manage`
    }
    navigate(redirectUrl)
  }

  if (!userId) {
    return null
  }

  return (
    <>
      <Hidden lgUp>
        <MobileDrawer
          bucketId={bucketId || ''}
          userId={userId}
          drawerSection={drawerSection}
          bucket={bucket}
          showWhatsAppButton={showWhatsAppButton}
          loading={loading}
          hasActiveWhatsAppIntegration={hasActiveWhatsAppIntegration}
          handleOpenAlbums={handleOpenAlbums}
          handleBack={handleBack}
          createBucket={createBucket}
          loadingMore={loadingMore}
          freemiumUserReachedMaxBuckets={freemiumUserReachedMaxBuckets}
          canCreateBucket={canCreateBucket}
          onClickWhatsApp={onClickWhatsApp}
          albums={albums}
          nextToken={nextToken}
          fetchMore={fetchMore}
        />
      </Hidden>
      <Hidden lgDown>
        <DesktopDrawer
          bucketId={bucketId || ''}
          userId={userId}
          drawerSection={drawerSection}
          bucket={bucket}
          showWhatsAppButton={showWhatsAppButton}
          loading={loading}
          hasActiveWhatsAppIntegration={hasActiveWhatsAppIntegration}
          handleOpenAlbums={handleOpenAlbums}
          handleBack={handleBack}
          createBucket={createBucket}
          loadingMore={loadingMore}
          freemiumUserReachedMaxBuckets={freemiumUserReachedMaxBuckets}
          canCreateBucket={canCreateBucket}
          onClickWhatsApp={onClickWhatsApp}
          albums={albums}
          nextToken={nextToken}
          fetchMore={fetchMore}
        />
      </Hidden>
    </>
  )
}
